import type { PermissionType, AccessSourceType, Permissions } from '@thinkalpha/common/contracts/permissions.js';
import type {
    BulkAccountRequestPayload,
    ConcreteAccount,
    NewAccount,
} from '@thinkalpha/trading-ws-client/contracts/account.js';
import Dataloader from 'dataloader';
import { createLegacyInstance } from 'src/api';
import type { PatchAccountParams } from 'src/lib/account/types';
import type { UpdateAccountPermission } from 'src/lib/accounts/types';
import { appConfig } from 'src/lib/config';

const axios = createLegacyInstance({ baseURL: appConfig.tradingApi });

export const getAccounts = async (access: PermissionType = 'reader'): Promise<ConcreteAccount[]> => {
    const { data } = await axios.get<ConcreteAccount[]>('/accounts', { params: { access } });
    return data;
};

export const getAccountPermissions = async (accountId: string): Promise<Permissions> => {
    const { data } = await axios.get<Permissions>(`/accounts/${accountId}/permissions`);
    return data;
};

export const createAccount = async (info: NewAccount): Promise<ConcreteAccount> => {
    const { data } = await axios.post<ConcreteAccount>(`/accounts`, info);
    return data;
};

export const createManyAccounts = async (accounts: NewAccount[]): Promise<ConcreteAccount[]> => {
    const accountPromises = [];
    for (const account of accounts) {
        accountPromises.push(axios.post<ConcreteAccount>(`/accounts`, account));
    }
    return await Promise.all(accountPromises).then((x) => x.map((y) => y.data));
};

export const patchAccount = async ({ accountId, changes }: PatchAccountParams): Promise<ConcreteAccount> => {
    const { data } = await axios.patch<ConcreteAccount>(`/accounts/${accountId}`, changes);
    return data;
};

export const updateAccountPermission = async ({
    entityId,
    entityType,
    accessType,
    accountId,
}: UpdateAccountPermission): Promise<ConcreteAccount> => {
    const { data } = await axios.put<ConcreteAccount>(`/accounts/${accountId}/permissions/${entityType}/${entityId}`, [
        { kind: 'allow', access: accessType },
    ]);
    return data;
};

export const removeAccountPermission = async ({
    entityId,
    entityType,
    accountId,
}: {
    entityId: string;
    entityType: AccessSourceType;
    accountId: string;
}): Promise<ConcreteAccount> => {
    const { data } = await axios.delete<ConcreteAccount>(
        `/accounts/${accountId}/permissions/${entityType}/${entityId}`,
    );
    return data;
};

export const getAccountsForSubject = async ({
    subjectId,
    forWhomType,
    access,
}: {
    subjectId: string;
    forWhomType: 'user' | 'firm' | 'group';
    access?: PermissionType;
}): Promise<ConcreteAccount[]> => {
    const { data } = await axios.get<ConcreteAccount[]>(`/accounts`, {
        params: { forWhom: subjectId, access, forWhomType, directAccessOnly: true },
    });
    return data;
};

export const getFirmMemberAccounts = async (firmId: string, memberId: string): Promise<ConcreteAccount[]> => {
    const { data } = await axios.get<ConcreteAccount[]>(`/accounts`, {
        params: { forWhom: memberId, forWhomType: 'user', directAccessOnly: true, firmId },
    });
    // Client-side safety net during mixed-version deploys: if trading-ws hasn't
    // shipped the firmId-honoring change yet, the server may return the user's
    // full account set. Filter again so the firm-scoped view never surfaces
    // cross-firm accounts even on a stale server.
    return data.filter((a) => a.firmId === firmId);
};

/**
 * @deprecated
 * This was in src/contracts/account.ts
 * before we migrated to platform-ws-client which does not have this type
 *
 */
export interface ExecutorListEntry {
    id: string;
    name: string;
}

export const getExecutorList = async (): Promise<ExecutorListEntry[]> => {
    const { data } = await axios.get<ExecutorListEntry[]>(`/executors`);
    return data;
};

export async function getUserAccounts(access: PermissionType = 'reader'): Promise<ConcreteAccount[]> {
    return axios
        .get<ConcreteAccount[]>('/accounts', { params: { access } })
        .then((x) => x.data.toSorted((a, b) => a.name?.localeCompare(b.name) ?? -1));
}

export async function getAccountById(id: string): Promise<ConcreteAccount> {
    const resp = await axios.get<ConcreteAccount>(`/accounts/${id}`);
    return resp.data;
}

export async function fetchAdminAccounts(): Promise<ConcreteAccount[]> {
    return getUserAccounts('admin');
}

export async function fetchOwnerAccounts(): Promise<ConcreteAccount[]> {
    return getUserAccounts('owner');
}

/**
 * Fetches accounts by their IDs in bulk
 * @private - use accountLoader instead
 */
async function getAccountsByIds(ids: string[]): Promise<ConcreteAccount[]> {
    const resp = await axios.post<ConcreteAccount[]>('/accounts/bulk-requests', {
        accounts: ids,
    } satisfies BulkAccountRequestPayload);
    return resp.data;
}

export const accountLoader = new Dataloader(
    async (ids: string[]): Promise<ConcreteAccount[]> => {
        const result = await getAccountsByIds(ids);

        const map = new Map(result.map((x) => [x.id, x] as const));

        return ids.map((id) => map.get(id)!);
    },
    { cache: false },
);
